import React from 'react'
import { Tabs } from '../../../components/Tabs/Tabs'
import { Tab } from 'react-bootstrap'
import { CollaborationSettings } from '../CollaborationSettings/CollaborationSettings'
import { PhoneSettings } from './PhoneSettings'
import { MFASettings } from './MFASettings'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { MainComplianceSettings } from './Compliance/MainComplianceSettings'
import { AIAssistantSettings } from './AIAssistantSettings'
import { AdminAssistantSettings } from './AdminAssistantSettings'
import { EmailTypeSettings } from './EmailType/EmailTypeSettings'
import { AccountSettings } from './Account/AccountSettings'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { LanguageSettings } from './Language/LanguageSettings'
import { BusinessEventSettings } from './BusinessEventSettings'
import { DataRetentionPolicies } from '../DataRetentionPolicies/DataRetentionPolicies'
import { EmailNotificationSettings } from './EmailNotifications/EmailNotificationSettings'
import './Settings.scss'
import { ConversationInsightsSettings } from './DiscussionTopics/ConversationInsightsSettings'

const DEFAULT_TAB_KEY = 'account'

const getAutoNavigationParameters = (hash: string) => {
  const selectedAccordionItem = hash.replace('#', '')
  return {
    selectedAccordionItem: selectedAccordionItem || undefined,
  }
}
const TabTitle = ({ title }: { title: string }) => <div className='d-flex align-items-center'>{title}</div>

export const Settings = () => {
  const navigate = useNavigate()

  const { hasScope } = useZocksAuth()
  const { settingsTab = DEFAULT_TAB_KEY } = useParams<{ settingsTab?: string }>()
  const { hash } = useLocation()
  const autoNavigation = getAutoNavigationParameters(hash)
  const hasMFAAdminScope = hasScope('**', 'mfa:admin', ['mfa:admin'])

  const { hasQuota } = useZocksQuota()
  const emailEnabled = hasQuota('integration.emailEnabled.value')
  const businessEventsEnabled = hasQuota('ai.businessEvent.value')

  const retentionEnabled = hasQuota('retention.enabled.value')

  const changeTab = (key: string | null) => {
    if (key) {
      navigate(`/workspace/setup/settings/${key}`, { replace: true })
    }
  }

  const BusinessEventSettingsTitle = (
    <div className='d-flex align-items-center zocks-gap-0.25'>
      Client Signals
      {!businessEventsEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
    </div>
  )

  const retentionTitle = (
    <div className='d-flex align-items-center zocks-gap-0.25'>
      Data Retention Policies
      {!retentionEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
    </div>
  )

  return (
    <div className='d-flex gap-5 settings-tab-wrapper'>
      <Tabs onSelect={changeTab} activeKey={settingsTab} id='settings-tabs' className='vertical-tab'>
        <Tab className='mt-2' eventKey='account' title={<TabTitle title='Account' />}>
          <AccountSettings />
        </Tab>

        <Tab className='mt-2' eventKey='compliance' title={<TabTitle title='Compliance' />}>
          <MainComplianceSettings defaultAccordionItemKey={autoNavigation.selectedAccordionItem} />
        </Tab>

        <Tab className='mt-2' eventKey='collaboration' title={<TabTitle title='Collaboration' />}>
          <CollaborationSettings />
        </Tab>

        <Tab className='mt-2' eventKey='phone' title={<TabTitle title='Shared phone line' />}>
          <PhoneSettings />
        </Tab>
        <Tab className='mt-2' eventKey='admin-assistant' title={<TabTitle title='Admin Assistant' />}>
          <AdminAssistantSettings />
        </Tab>

        {emailEnabled ? (
          <Tab className='mt-2' eventKey='email-type' title={<TabTitle title='Email type' />}>
            <EmailTypeSettings />
          </Tab>
        ) : null}

        <Tab className='mt-2' eventKey='email-notifications' title={<TabTitle title='Email notifications' />}>
          <EmailNotificationSettings />
        </Tab>

        <Tab className='mt-2' eventKey='ai-assistant' title={<TabTitle title='AI Assistant' />}>
          <AIAssistantSettings />
        </Tab>

        {hasMFAAdminScope ? (
          <Tab className='mt-2' eventKey='mfa' title={<TabTitle title='2-factor authentication' />}>
            <MFASettings />
          </Tab>
        ) : null}

        <Tab className='mt-2' eventKey='language' title={<TabTitle title='Language' />}>
          <LanguageSettings />
        </Tab>
        <Tab className='mt-2' eventKey='data-retention' title={retentionTitle}>
          <DataRetentionPolicies />
        </Tab>

        <Tab className='mt-2' eventKey='business-events' title={BusinessEventSettingsTitle}>
          <BusinessEventSettings />
        </Tab>
        <Tab className='mt-2' eventKey='insights' title={<TabTitle title='Insights' />}>
          <ConversationInsightsSettings />
        </Tab>
      </Tabs>
    </div>
  )
}
